import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  enumContainer,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LoadingSpinner,
  mixins,
  omitNullsy,
  useLocationQuery,
  useMaximizeWrapper,
} from '@wallaby-ui/core';
import {
  AlertCategory,
  AlertCategoryIcon,
  AlertEpisodeActions,
  AlertEpisodeDisplayedState,
  AlertEpisodeStack,
  AlertEpisodeTooltip,
  AlertListEpisodeDto,
  AlertPatientListRowDto,
  AlertStateChip,
  AlertWeightBadge,
  ALERT_CATEGORY_ENUM,
  ALERT_DISPLAYED_STATE_ENUM,
  ALERT_ORIGIN_ENUM,
  ALERT_PATIENT_LIST_DEFAULT_SORT_PROPERTY,
  ALERT_PATIENT_LIST_SORT_PROPERTIES,
  clampPageNumber,
  countSelectedFilters,
  DEFAULT_PAGE_SIZE,
  getDefaultMTOptions,
  LightDateRangeFilter,
  LightFilterPanel,
  LightNumberRangeFilter,
  LightSelectedFilters,
  LightSelectedFiltersPane,
  LightSelectFilter,
  LightTextFilter,
  MaterialTableWrapper,
  TablePaging,
  TableSorting,
  useAlertEpisodeDisposition,
  useAlertPatientList,
  useAlertSnoozeOptions,
  useFormatters,
  usePermissions,
  UserFilterDto,
  useUserFilters,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useNavigate} from 'react-router-dom';

import {resolveFloatingFirstActive} from './alertBoardFilters';
import {alertEpisodeHref} from './alertEpisodeHref';
import {AlertOverflowText} from './AlertOverflowText';
import {
  ALERT_LIST_COMPONENT,
  AlertListPageFilters,
  buildAlertListExportParams,
  buildAlertListParams,
  buildAlertListSelectedFilters,
  DEFAULT_ALERT_LIST_FILTERS,
  DEFAULT_ALERT_LIST_PAGING,
  DEFAULT_ALERT_LIST_SORTING,
  normalizeAlertListFiltersForCompare,
} from './alertListFilters';
import {withServerSort} from './alertTableSort';
import {useMerieAlertCriteriaRenderer} from './MerieAlertCriteriaChips';
import {useAlertBoardAlertOptions} from './useAlertBoardAlertOptions';
import {useAlertExport} from './useAlertExport';
import {useAppSelector} from 'src/js/store_hooks';

const ALERT_LIST_SPLIT_CONFIG = {
  sizes: [233, 767],
  minSize: [64, 100],
  layoutId: '/LightSplitLayout.AlertListLayout',
};
/** The product's name in the tooltip of an activation or a deactivation, which the system does. */
const SYSTEM_ACTOR_NAME = 'MERIE';

type Props = {
  config: ConfigObjectType;
};

type FiltersData = {
  alertsOpen: boolean;
  alerts: AlertListPageFilters;
  alertsSorting: TableSorting;
  alertsPaging: TablePaging;
};

/**
 * The alerts list (PDW-183 / FE PDW-256): a queue of patients ordered by the summed weight of their Active alerts, each
 * patient with all of their matching episodes beneath. Core filters, sums, sorts and pages by patient; the page maps its
 * filter to the query string and works the episodes through the same disposition write as the Alerts panel. Reachable
 * only with the Use alerts right, which the navigation and the BE both require.
 */
export const AlertListLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const component = ALERT_LIST_COMPONENT;
  const storageIdPrefix = '/alertList';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(
    () => ({
      alertsOpen: query.alertsOpen ?? true,
      alerts: query?.alerts ?? DEFAULT_ALERT_LIST_FILTERS,
      alertsSorting: query?.alertsSorting ?? DEFAULT_ALERT_LIST_SORTING,
      alertsPaging: query?.alertsPaging ?? DEFAULT_ALERT_LIST_PAGING,
    }),
    [query]
  );
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({alerts: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <AlertListLayoutInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
};

type EpisodeActions = {
  act: (episodeId: number, post: Parameters<React.ComponentProps<typeof AlertEpisodeActions>['onAction']>[0]) => void;
  isPending: boolean;
  snoozeOptions: React.ComponentProps<typeof AlertEpisodeActions>['snoozeOptions'];
  onSnoozeOptionsNeeded: () => void;
};

const isMuted = (state: AlertEpisodeDisplayedState) => state !== 'ACTIVE';

/**
 * One table row per patient: the patient and the total in their own cells, every episode column a stack of one line
 * per episode, as the design's merged cells. The History column is another story.
 */
function useColumns(config: ConfigObjectType, actions: EpisodeActions): Column<AlertPatientListRowDto>[] {
  const {t, tEnum, formatDate} = useFormatters();
  const navigate = useNavigate();
  const renderCriteria = useMerieAlertCriteriaRenderer(config);
  return useMemo(() => {
    const top: React.CSSProperties = {verticalAlign: 'top'};
    const stack = (
      row: AlertPatientListRowDto,
      render: (episode: AlertListEpisodeDto) => React.ReactNode,
      justify?: 'center' | 'flex-end'
    ) => <AlertEpisodeStack items={row.episodes} getKey={(episode) => episode.episodeId} render={render} justify={justify} />;
    const columns: Column<AlertPatientListRowDto>[] = [
      {
        field: 'fullName',
        title: t('alert.list.column.patient'),
        cellStyle: top,
        render: (row) => (
          <div css={styles.patient}>
            <span data-name={'dl-alert-list-patient'}>{row.fullName ?? ''}</span>
            {row.identificationNumber && <div css={styles.personalId}>{row.identificationNumber}</div>}
          </div>
        ),
      },
      {
        field: 'totalAlertWeight',
        title: t('alert.list.column.totalAlertWeight'),
        width: '110px',
        cellStyle: top,
        render: (row) => (
          <div css={styles.total} data-name={'dl-alert-list-total'}>
            {row.totalAlertWeight}
          </div>
        ),
      },
      {
        field: 'isGlobal',
        title: '',
        sorting: false,
        width: '44px',
        cellStyle: top,
        render: (row) =>
          stack(
            row,
            (episode) => (
              <span css={styles.category} data-muted={isMuted(episode.displayedState)}>
                <AlertCategoryIcon isGlobal={episode.isGlobal} fontSize={12} />
              </span>
            ),
            'center'
          ),
      },
      {
        field: 'name',
        title: t('alert.board.column.alert'),
        sorting: false,
        cellStyle: top,
        render: (row) =>
          stack(row, (episode) => {
            const href = alertEpisodeHref(episode);
            const nameProps = {
              'css': styles.name,
              'data-link': Boolean(href),
              'data-muted': isMuted(episode.displayedState),
              'data-deleted': episode.displayedState === 'DELETED',
              'data-name': 'dl-alert-list-name',
            };
            return (
              <AlertEpisodeTooltip
                tooltip={episode.tooltip}
                displayedState={episode.displayedState}
                systemActorName={SYSTEM_ACTOR_NAME}
                renderCriteria={renderCriteria}
              >
                {href ? (
                  <a
                    {...nameProps}
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(href);
                    }}
                  >
                    {episode.name}
                  </a>
                ) : (
                  <span {...nameProps}>{episode.name}</span>
                )}
              </AlertEpisodeTooltip>
            );
          }),
      },
      {
        field: 'weight',
        title: t('alert.weight'),
        width: '96px',
        cellStyle: top,
        // the alert's weight on every row, greyed out on the rows that are not Active: only those count toward the total
        render: (row) =>
          stack(row, (episode) => <AlertWeightBadge weight={episode.weight} variant='board' muted={isMuted(episode.displayedState)} />),
      },
      {
        field: 'component',
        title: t('hai.properties.origin'),
        sorting: false,
        cellStyle: top,
        render: (row) =>
          stack(row, (episode) => {
            const component = episode.tooltip.filterComponent;
            return (
              <AlertOverflowText
                text={tEnum(ALERT_ORIGIN_ENUM, component)}
                textCss={styles.muted}
                data-muted={isMuted(episode.displayedState)}
              />
            );
          }),
      },
      {
        field: 'firstActiveOn',
        title: t('alert.list.column.firstActive'),
        width: '120px',
        cellStyle: top,
        render: (row) =>
          stack(row, (episode) => (
            <span css={styles.muted} data-muted={isMuted(episode.displayedState)}>
              {formatDate(episode.firstActiveOn)}
            </span>
          )),
      },
      {
        field: 'displayedState',
        title: t('alert.list.column.state'),
        sorting: false,
        width: '130px',
        cellStyle: top,
        render: (row) =>
          stack(row, (episode) => (
            <AlertStateChip state={episode.displayedState} snoozeExpiresOn={episode.snoozeExpiresOn} variant='list' />
          )),
      },
      {
        field: 'actions',
        title: t('alert.list.column.actions'),
        sorting: false,
        width: '140px',
        cellStyle: top,
        render: (row) =>
          stack(
            row,
            (episode) => (
              <AlertEpisodeActions
                actions={episode.availableActions}
                snoozeOptions={actions.snoozeOptions}
                onSnoozeOptionsNeeded={actions.onSnoozeOptionsNeeded}
                onAction={(post) => actions.act(episode.episodeId, post)}
                disabled={actions.isPending}
                variant='list'
              />
            ),
            'flex-end'
          ),
      },
    ];
    // MERIE sorts the page: the table keeps its rows in the server's order and only shows the sort on the header
    return columns.map((v) => ({...v, customSort: () => 0}));
  }, [t, tEnum, formatDate, navigate, renderCriteria, actions]);
}

const AlertListLayoutInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const formatters = useFormatters();
  const {t} = formatters;
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const {isLoading: permissionsLoading, userRoles} = usePermissions(config);
  const canUseAlerts = Boolean(userRoles.ALERT_USE_ROLE);
  const {alerts} = filters;
  const hasFilters = !isEqual(omitNullsy(normalizeAlertListFiltersForCompare(alerts)), omitNullsy(DEFAULT_ALERT_LIST_FILTERS));

  // A floating range ("Last 31 days") is resolved again for today, since MERIE takes the dates only
  const floating = resolveFloatingFirstActive(alerts);
  useEffect(() => {
    if (floating && (floating.from !== alerts.firstActiveFrom || floating.to !== alerts.firstActiveTo)) {
      changeFilters({alerts: {...alerts, firstActiveFrom: floating.from, firstActiveTo: floating.to}});
    }
  }, [floating?.from, floating?.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const floatingPending = Boolean(floating && (floating.from !== alerts.firstActiveFrom || floating.to !== alerts.firstActiveTo));

  const params = useMemo(
    () => buildAlertListParams(alerts, filters.alertsPaging, filters.alertsSorting),
    [alerts, filters.alertsPaging, filters.alertsSorting]
  );
  const {page, isLoading, refetch} = useAlertPatientList(config, params, canUseAlerts && !floatingPending);

  // The file repeats each patient on every one of their episode rows, while the export limit still counts patients,
  // as the page does: the limit dialog says patients (TASK_ALERT_EXCEL_EXPORT §6.1)
  const exportParams = useMemo(() => buildAlertListExportParams(alerts, filters.alertsSorting), [alerts, filters.alertsSorting]);
  const {
    handleXlsxExport,
    isExporting,
    warningDialog: exportWarningDialog,
  } = useAlertExport({
    url: config.service.url.alertListExport,
    maxExportRowsUrl: config.service.url.maxExportRows,
    params: exportParams,
    totalCount: page?.totalElements,
    limitTitleKey: 'alert.list.export.limit.title',
    limitTextKey: 'alert.list.export.limit.text',
  });

  // the episode actions: the panel's write path, and the whole page read again after each action (an action moves a
  // row's state and changes the patient's total)
  const [snoozeNeeded, setSnoozeNeeded] = useState(false);
  const snoozeOptions = useAlertSnoozeOptions(config, snoozeNeeded);
  const onSnoozeOptionsNeeded = useCallback(() => setSnoozeNeeded(true), []);
  const {act, isPending} = useAlertEpisodeDisposition(config, refetch);
  const episodeActions = useMemo<EpisodeActions>(
    () => ({act, isPending, snoozeOptions, onSnoozeOptionsNeeded}),
    [act, isPending, snoozeOptions, onSnoozeOptionsNeeded]
  );
  const columns = useColumns(config, episodeActions);

  const {options: alertOptions, isLoading: alertOptionsLoading, getAlertLabel} = useAlertBoardAlertOptions(config, canUseAlerts);

  const filterInputsRef = useRef<HTMLDivElement>(null);
  // Fullscreen filter panel (PDM-2842): this wrapper IS the panel's card (16px padding, no bottom padding so the
  // content scrolls to the card edge); the panel itself owns the button and keeps the flag consistent with "open".
  const {
    maximized: filterPanelMaximized,
    setMaximized: setFilterPanelMaximized,
    MaximizeWrapper: FilterPanelMaximizeWrapper,
  } = useMaximizeWrapper('filter-panel-wrapper', {innerStyle: {rowGap: 16, paddingBottom: 0}});
  const filtersRef = useRef<HTMLDivElement | null>(null);

  const selectedFilters = buildAlertListSelectedFilters(alerts, formatters, {getAlertLabel});
  const selectedFiltersCount = countSelectedFilters(selectedFilters, alerts);

  if (permissionsLoading) return <LoadingSpinner isVisible text='' />;
  if (!canUseAlerts) return null;

  const changeAlerts = (newAlerts: AlertListPageFilters) =>
    changeFilters({alerts: newAlerts, alertsPaging: {...filters.alertsPaging, page: 0}});
  const categoryOptions = enumMap[ALERT_CATEGORY_ENUM] ?? [];
  const originOptions = enumMap[ALERT_ORIGIN_ENUM] ?? [];
  const stateOptions = enumMap[ALERT_DISPLAYED_STATE_ENUM] ?? [];
  // The default order leads with the total alert weight descending, which its header shows while the user sorts nothing
  const shownSort: TableSorting =
    filters.alertsSorting.field && filters.alertsSorting.direction
      ? filters.alertsSorting
      : {field: ALERT_PATIENT_LIST_DEFAULT_SORT_PROPERTY, direction: 'desc'};
  const tableColumns = withServerSort(columns, shownSort);

  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={t('navigation.route.alertList')} />
      <LightSplitLayout
        initSizes={ALERT_LIST_SPLIT_CONFIG.sizes}
        minSizes={ALERT_LIST_SPLIT_CONFIG.minSize}
        storageId={ALERT_LIST_SPLIT_CONFIG.layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.alertsOpen, false]}
        autoFitFilterRows={{contentRef: filterInputsRef, enabled: filters.alertsOpen}}
        hidden={[false, false]}
      >
        <FilterPanelMaximizeWrapper>
          <LightFilterPanel
            open={filters.alertsOpen}
            toggleOpen={() => changeFilters({alertsOpen: !filters.alertsOpen})}
            hasActiveFilter={hasFilters}
            scrollAreaRef={filtersRef}
            filterInputsRef={filterInputsRef}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: alerts,
              setFilter: (newFilter) => changeFilters({alerts: newFilter}),
              config,
              normalizeForCompare: normalizeAlertListFiltersForCompare,
            }}
            selectedFilters={
              selectedFiltersCount.total > 0 ? (
                <LightSelectedFiltersPane
                  selectedFilters={selectedFilters}
                  tableFilters={alerts}
                  changeTableFilters={(newTableFilters) => changeAlerts(newTableFilters as AlertListPageFilters)}
                  formatters={formatters}
                />
              ) : null
            }
            selectedFiltersCount={selectedFiltersCount.total}
            maximize={{maximized: filterPanelMaximized, setMaximized: setFilterPanelMaximized}}
          >
            <LightTextFilter
              config={{name: 'patientFullName', ui: {label: 'filter.patient.fullName'}}}
              value={alerts.patientFullName}
              onChange={(newValue) => changeAlerts({...alerts, patientFullName: newValue ?? undefined})}
            />
            <LightTextFilter
              config={{name: 'patientIdentificationNumber', ui: {label: 'filter.patient.identificationNumber'}}}
              value={alerts.patientIdentificationNumber}
              onChange={(newValue) => changeAlerts({...alerts, patientIdentificationNumber: newValue ?? undefined})}
            />
            <LightTextFilter
              config={{name: 'patientSrcId', ui: {label: 'filter.patient.sourceId'}}}
              value={alerts.patientSrcId}
              onChange={(newValue) => changeAlerts({...alerts, patientSrcId: newValue ?? undefined})}
            />
            <LightNumberRangeFilter
              config={{name: 'totalAlertWeight', ui: {label: 'alert.list.column.totalAlertWeight', min: 0, max: 500}}}
              value={
                alerts.totalAlertWeightFrom != null && alerts.totalAlertWeightTo != null
                  ? {start: alerts.totalAlertWeightFrom, end: alerts.totalAlertWeightTo}
                  : null
              }
              onChange={(newValue) => changeAlerts({...alerts, totalAlertWeightFrom: newValue?.start, totalAlertWeightTo: newValue?.end})}
            />
            <LightSelectFilter
              config={{name: 'categories', ui: {label: 'alert.board.filter.category'}}}
              options={categoryOptions}
              multi
              maxWidth={'176px'}
              value={alerts.categories}
              onChange={(newValue: AlertCategory[] | null) =>
                changeAlerts({...alerts, categories: newValue?.length ? newValue : undefined})
              }
            />
            <LightSelectFilter
              config={{name: 'userFilterIds', ui: {label: 'alert.board.filter.alert'}}}
              options={alertOptions}
              loading={alertOptionsLoading}
              multi
              value={alerts.userFilterIds?.map(String)}
              onChange={(newValue: string[] | null) =>
                changeAlerts({...alerts, userFilterIds: newValue?.length ? newValue.map(Number) : undefined})
              }
            />
            <LightNumberRangeFilter
              config={{name: 'weight', ui: {label: 'alert.weight', min: 0, max: 100}}}
              value={alerts.weightFrom != null && alerts.weightTo != null ? {start: alerts.weightFrom, end: alerts.weightTo} : null}
              onChange={(newValue) => changeAlerts({...alerts, weightFrom: newValue?.start, weightTo: newValue?.end})}
            />
            <LightSelectFilter
              config={{name: 'components', ui: {label: 'hai.properties.origin'}}}
              options={originOptions}
              multi
              value={alerts.components}
              onChange={(newValue: string[] | null) => changeAlerts({...alerts, components: newValue?.length ? newValue : undefined})}
            />
            <LightDateRangeFilter
              config={{name: 'firstActive', ui: {label: 'alert.list.column.firstActive'}}}
              value={
                alerts.firstActiveFrom && alerts.firstActiveTo
                  ? {startDate: alerts.firstActiveFrom, endDate: alerts.firstActiveTo, shortcut: alerts.firstActiveFloatingShortcutName}
                  : null
              }
              onChange={(newValue) =>
                changeAlerts({
                  ...alerts,
                  firstActiveFrom: newValue?.startDate,
                  firstActiveTo: newValue?.endDate,
                  firstActiveFloatingShortcutName: newValue?.shortcut,
                })
              }
              dateOnly
              showShortcuts
            />
            <LightSelectFilter
              config={{name: 'states', ui: {label: 'alert.list.column.state'}}}
              options={stateOptions}
              multi
              maxWidth={'176px'}
              isDefaultValue={isEqual(alerts.states, DEFAULT_ALERT_LIST_FILTERS.states)}
              value={alerts.states}
              onChange={(newValue: AlertEpisodeDisplayedState[] | null) =>
                changeAlerts({...alerts, states: newValue?.length ? newValue : undefined})
              }
            />
          </LightFilterPanel>
        </FilterPanelMaximizeWrapper>
        <div css={muiStyles.listColumn} data-name={'dl-alert-list'}>
          <div css={muiStyles.tableCard} data-name={'dl-alert-list-table'}>
            <IconHeaderWithSearch
              iconName={'bellIcon'}
              useFixedViewBox={'0 0 448 512'}
              iconCss={muiStyles.headerIcon}
              label={'alert.panel.title'}
              labelStyle={{fontSize: 19}}
              childrenWrapperStyle={{gap: 10}}
            >
              <LightSelectedFilters
                align='flex-start'
                marginLeft='8px'
                hasFilters={hasFilters}
                handleResetFilters={() => changeAlerts(DEFAULT_ALERT_LIST_FILTERS)}
                hasSorting={!isEqual(filters.alertsSorting, DEFAULT_ALERT_LIST_SORTING)}
                handleResetSorting={() => changeFilters({alertsSorting: DEFAULT_ALERT_LIST_SORTING})}
                hasXlsxExport
                handleXlsxExport={handleXlsxExport}
                xlsxExportDisabled={isExporting || floatingPending}
              />
              {exportWarningDialog}
            </IconHeaderWithSearch>
            <MaterialTableWrapper
              variant='big'
              options={{
                ...getDefaultMTOptions(page?.size ?? DEFAULT_PAGE_SIZE),
                padding: 'dense',
              }}
              useMaxHeight
              columns={tableColumns}
              data={page?.content ?? []}
              isLoading={isLoading || floatingPending}
              totalCount={page?.totalElements}
              page={page?.number}
              totalPages={page?.totalPages}
              onChangePage={(newPage, pageSize) => changeFilters({alertsPaging: {...filters.alertsPaging, page: newPage, pageSize}})}
              onChangeRowsPerPage={(pageSize) =>
                changeFilters({
                  alertsPaging: {
                    ...filters.alertsPaging,
                    pageSize,
                    page: clampPageNumber(filters.alertsPaging.page, pageSize, page?.totalElements),
                  },
                })
              }
              controlledPageSize={filters.alertsPaging.pageSize ?? DEFAULT_PAGE_SIZE}
              onOrderChange={(colIndex, direction) => {
                const field = tableColumns[colIndex]?.field as string;
                if (!ALERT_PATIENT_LIST_SORT_PROPERTIES.includes(field as any)) return;
                // the table echoes the shown sort back on mount: the default order shown on the total is not the user's sort
                if (field === shownSort.field && direction === shownSort.direction) return;
                changeFilters({
                  alertsSorting: {field, direction},
                  alertsPaging: {...filters.alertsPaging, page: 0},
                });
              }}
              controlledSort={shownSort}
              scrollStorageId='/AlertListLayout.scrollXY'
            />
          </div>
        </div>
      </LightSplitLayout>
    </div>
  );
};

/* the Alerts Table design; a non-active row is greyed, a deleted name struck through */
const styles = {
  patient: css`
    /* the name level with the first episode's line, whose content sits in the middle of 48px */
    padding-top: 13px;
    white-space: nowrap;
  `,
  personalId: css`
    font-size: 12px;
    color: var(--dlThemeGray-500);
    margin-top: 3px;
  `,
  total: css`
    padding-top: 13px;
    font-size: 14px;
    font-family: var(--dlFontFamilySemiBold);
    color: var(--dlThemeGray-800);
  `,
  category: css`
    display: inline-flex;
    vertical-align: middle;
    color: var(--dlThemeGray-400);
    &[data-muted='true'] {
      color: var(--dlThemeGray-300);
    }
  `,
  name: css`
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    font-size: 14px;
    font-family: var(--dlFontFamilySemiBold);
    color: var(--dlThemeGray-800);
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    /* a link to the alert's source record stays teal on hover and focus, as the design's links (the global link hover is blue) */
    &[data-link='true'] {
      &,
      &:hover,
      &:focus {
        color: var(--dlTheme-500);
      }
      text-decoration: underline;
      cursor: pointer;
    }
    &[data-muted='true'],
    &[data-muted='true']:hover,
    &[data-muted='true']:focus {
      color: var(--dlThemeGray-400);
    }
    &[data-deleted='true'] {
      text-decoration: line-through;
    }
    &[data-link='true'][data-deleted='true'] {
      text-decoration: underline line-through;
    }
  `,
  muted: css`
    font-size: 13.5px;
    white-space: nowrap;
    color: var(--dlThemeGray-600);
    &[data-muted='true'] {
      color: var(--dlThemeGray-400);
    }
  `,
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  /* the lower pane never scrolls: the table card fills it and only the table inside it scrolls */
  listColumn: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  `,
  tableCard: css`
    ${mixins.contentBox};
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    padding: 16px;
    /* one band per patient as the Alerts Table design, a shade darker than the app's other tables */
    --dlAgGridRowStripping: var(--dlAlertListRowStripe);
    & > .ag-body-viewport {
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
    }
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
});
